
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserOrganisationCategory from 'Models/Security/Acl/UserOrganisationCategory';
import VisitorsOrganisationCategory from 'Models/Security/Acl/VisitorsOrganisationCategory';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IOrganisationCategoryEntityAttributes extends IModelAttributes {
	category: Enums.organisationCategory;
	markup: number;


}

@entity('OrganisationCategoryEntity', 'Organisation Category')
export default class OrganisationCategoryEntity extends Model
	implements IOrganisationCategoryEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserOrganisationCategory(),
		new VisitorsOrganisationCategory(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<OrganisationCategoryEntity, Enums.organisationCategory>()
	@CRUD({
		name: 'Category',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.organisationCategoryOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.organisationCategoryOptions),
		displayFunction: (attr: Enums.organisationCategory) => Enums.organisationCategoryOptions[attr],
	})
	public category: Enums.organisationCategory;
	

	
	@Validators.Numeric()
	@observable
	@attribute<OrganisationCategoryEntity, number>()
	@CRUD({
		name: 'Markup',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public markup: number;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IOrganisationCategoryEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IOrganisationCategoryEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.category !== undefined) {
				this.category = attributes.category;
			}
			if (attributes.markup !== undefined) {
				this.markup = attributes.markup;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(OrganisationCategoryEntity.prototype, 'created');
CRUD(modifiedAttr)(OrganisationCategoryEntity.prototype, 'modified');
